import React, { useEffect, useRef, useState } from 'react';
import { Breadcrumb, Button, Col, DatePicker, Descriptions, Row, Spin, Table, InputNumber, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { Link, RouteComponentProps } from 'react-router-dom';
import { TextFormat } from 'react-jhipster';
import { useReactToPrint } from 'react-to-print';

import { APP_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';

import { getEcole } from '../ecole/ecole.reducer';
import { getProfesseur } from './personnel.reducer';
import { getProfesseurCours, updateCoursVolume } from '../cours/cours-reducer';

/* ================= TYPES ================= */

interface CoursTableRow {
  key: string;
  id: string;
  date: string;
  volume: string;
  cours: string;
}

/* ================= COMPONENT ================= */

export const CoursProfesseur = (props: RouteComponentProps<{ ecoleid: string; id: string }>) => {
  const dispatch = useAppDispatch();

  const listCours = useAppSelector(state => state.cours.entities);
  const currentEcole = useAppSelector(state => state.ecole.entity);
  const professeur = useAppSelector(state => state.professeur.entity);
  const loading = useAppSelector(state => state.cours.loading);
  const updating = useAppSelector(state => state.cours.updating);

  const canEditVolume = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [
      AUTHORITIES.ADMIN,
      AUTHORITIES.DIRECTEUR,
      AUTHORITIES.DIRECTEUR_ETUDE,
      AUTHORITIES.SURVEILLANT_GENERAL,
    ])
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingVolume, setEditingVolume] = useState<number>(0);
  const [download, setDownload] = useState(false);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  const componentRef = useRef<HTMLDivElement>(null);

  /* ================= EFFECTS ================= */

  useEffect(() => {
    dispatch(getEcole(props.match.params.ecoleid));
    dispatch(getProfesseur(props.match.params.id));
    loadCours();
  }, []);

  const loadCours = () => {
    dispatch(getProfesseurCours({ profId: props.match.params.id }));
  };

  /* ================= HANDLERS ================= */

  const handleStartEdit = (id: string, currentVolume: string) => {
    setEditingId(id);
    setEditingVolume(parseInt(currentVolume, 10) || 0);
  };

  const handleSaveVolume = (coursId: string) => {
    dispatch(
      updateCoursVolume({
        coursId,
        volume: String(editingVolume),
        profId: props.match.params.id,
      })
    );
    setEditingId(null);
  };

  const handleCancelEdit = () => setEditingId(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const onChange = (_value: any, dateString: [string, string]) => {
    if (dateString[0] && dateString[1]) {
      setDateDebut(dateString[0]);
      setDateFin(dateString[1]);

      dispatch(
        getProfesseurCours({
          profId: props.match.params.id,
          startDate: dateString[0],
          endDate: dateString[1],
        })
      );
      setDownload(true);
    } else {
      setDateDebut('');
      setDateFin('');
      loadCours();
      setDownload(false);
    }
  };

  /* ================= TABLE DATA ================= */

  let totalH = 0;

  const data: CoursTableRow[] = listCours.map(c => {
    totalH += parseInt(c.volume ?? '0', 10);

    const coursLabel = c.calendarEntry
      ? `${c.calendarEntry?.matiere?.nom}, ${c.calendarEntry?.heureDebut} - ${c.calendarEntry?.heureFin}, ${c.calendarEntry?.classe?.nom}`
      : '';

    return {
      key: c.id,
      id: c.id,
      date: c.date,
      volume: c.volume,
      cours: coursLabel,
    };
  });

  const columns: ColumnsType<CoursTableRow> = [
    {
      title: 'Cours',
      dataIndex: 'cours',
      key: 'cours',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: date => <TextFormat type="date" value={date} format={APP_DATE_FORMAT} />,
    },
    {
      title: 'Volume (min)',
      dataIndex: 'volume',
      key: 'volume',
      render: (volume, record) =>
        canEditVolume && editingId === record.id ? (
          <Space size="small">
            <InputNumber
              min={1}
              max={600}
              value={editingVolume}
              onChange={value => setEditingVolume(value ?? 0)}
              size="small"
              style={{ width: 80 }}
              autoFocus
            />
            <Button
              type="text"
              size="small"
              icon={<CheckOutlined style={{ color: 'green' }} />}
              onClick={() => handleSaveVolume(record.id)}
              loading={updating}
            />
            <Button type="text" size="small" icon={<CloseOutlined style={{ color: 'red' }} />} onClick={handleCancelEdit} />
          </Space>
        ) : (
          <Space size="small">
            <span>{volume}</span>
            {canEditVolume && (
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleStartEdit(record.id, volume)} />
            )}
          </Space>
        ),
    },
  ];

  const today = new Date();

  /* ================= RENDER ================= */

  return (
    <div>
      <Breadcrumb className="p-4">
        <Breadcrumb.Item>
          <Link to={`/home/${currentEcole?.id}`}>{currentEcole?.nom}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/personnel/${currentEcole?.id}`}>Personnel</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/personnel/${currentEcole?.id}/${professeur?.id}/details`}>Details</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/personnel/${currentEcole?.id}/${professeur?.id}/cours`}>Cours</Link>
        </Breadcrumb.Item>
      </Breadcrumb>

      <Row className="p-2 justify-content-center">
        <Col lg={10} xs={24}>
          <DatePicker.RangePicker placeholder={['Date de début', 'Date de fin']} separator="au " onChange={onChange} />
          &nbsp;&nbsp;
          {download && (
            <Button onClick={handlePrint} type="primary">
              <FontAwesomeIcon icon={faPrint} /> Exporter
            </Button>
          )}
        </Col>
      </Row>

      <div ref={componentRef} className="p-4">
        <Row className="p-4">
          <Col lg={10} xs={24}>
            <Descriptions column={1}>
              <Descriptions.Item label="Matricule">{professeur?.matricule}</Descriptions.Item>
              <Descriptions.Item label="Classes">{professeur?.classes?.map(c => c.nom).join(', ')}</Descriptions.Item>
              <Descriptions.Item label="Matières">{professeur?.matieresEcole?.map(m => m.nom).join(', ')}</Descriptions.Item>
              <Descriptions.Item label="Date">
                {today.getDate()}-{today.getMonth() + 1}-{today.getFullYear()}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col>
            <h3 className="center">
              {professeur?.prenom} {professeur?.nom}
            </h3>
          </Col>
        </Row>

        <Row className="table-reponsive p-4 justify-content-center">
          <Col lg={20} xs={24}>
            <h4 className="center">Historiques des Cours {dateDebut && dateFin ? `du ${dateDebut} au ${dateFin}` : ''}</h4>

            <Spin spinning={loading} tip="Chargement...">
              <Table
                dataSource={[...data].reverse()}
                columns={columns}
                size="small"
                pagination={false}
                bordered
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                      <Table.Summary.Cell index={0} />
                      <Table.Summary.Cell index={1}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        {totalH ? `${totalH} Minutes ≈ ${Math.floor(totalH / 60)}H:${totalH % 60}mn` : '0'}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Spin>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CoursProfesseur;
